/**
 * ¿La sesión con el socio (Software) sigue siendo nuestra? Y si no, qué decir.
 *
 * POR QUÉ EXISTE
 * --------------
 * TEST, 06-oct-2026. La sesión de una clienta de Software se cerró por
 * inactividad a las 18:37 y le devolvimos el control a Software. Al día
 * siguiente el portal mostraba «Ventana 24 h · quedan 5 h 43 min» y el
 * compositor abierto; se reasignó, la asesora escribió dos mensajes y los dos
 * quedaron «enviado» sin llegarle a nadie.
 *
 * La ventana no mentía: medía el número DE SOFTWARE, al que la clienta le había
 * escrito. Lo que faltaba era otra pregunta —¿el número es nuestro para
 * usarlo?—, y con la sesión devuelta no lo es: lo que se escribe sale por
 * NUESTRO número a alguien que nunca nos escribió. La responde el backend
 * (`portal_api/operacion/sesion_del_socio.py`, sobre `relay.devuelta_al_socio`)
 * y acá solo se redacta.
 *
 * Puro: lo corre `tests/unit/test_la_sesion_devuelta_se_ve.py` en Node con lo
 * que devuelve el detalle del portal de verdad.
 */
import { fechaHoraEnLima } from './documentos'
import type { SesionSocio } from './types/portal'

/**
 * El `error` del 409 con que el backend rechaza responder o devolver al agente
 * sobre una sesión devuelta (`portal_api.MOTIVO_SESION_DEVUELTA`, cruzado por test).
 */
export const SESION_DEVUELTA = 'session_returned_to_partner'

/**
 * La sesión volvió a un socio dueño de OTRO número: escribirle al cliente
 * desde el portal no le llega. Con el puente de TEST (`coe`) es `false`
 * aunque esté devuelta: el número es el nuestro.
 */
export function sinCanalConElCliente(conv: { sesion_socio?: SesionSocio | null }): boolean {
  const s = conv.sesion_socio
  return Boolean(s && s.estado === 'devuelta' && !s.puede_escribir)
}

/** `software` → `Software`. Sin socio conocido, «el socio». */
export function nombreDelSocio(socio: string | null | undefined): string {
  const s = (socio || '').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'el socio'
}

/**
 * Cuántas horas desde el pase es nuestra la sesión del socio: 48 desde el
 * 22-oct-2026 (eran 24). El número es del backend
 * (`reloj_de_sesion.VENTANA_MIN`) y vive acá solo para redactar; un test lo
 * cruza para que los textos del portal no se queden con el anterior.
 */
export const HORAS_DE_LA_SESION = 48

/**
 * Por qué se devolvió, en palabras del asesor. Las claves son las causas que
 * escribe `relay.liberacion`: el `reason` del contrato cuando coincide, y las
 * dos que el contrato agrupa en `inactive` (`ventana_vencida`,
 * `salida_del_cliente`). Cruzadas por test contra los que emite el backend.
 */
export const CAUSAS_DE_DEVOLUCION: Record<string, string> = {
  inactive: 'se cerró por inactividad del cliente',
  ventana_vencida: `se cumplieron las ${HORAS_DE_LA_SESION} h de la sesión`,
  salida_del_cliente: 'el cliente pidió salir',
  completed: 'el expediente quedó completo',
  advisor_resolved: 'un asesor la marcó resuelta',
  partner_requested: 'el socio la pidió de vuelta',
}

export interface AvisoDeSesionDevuelta {
  /** El chip de la cabecera y de la lista: «Devuelta a Software». */
  chip: string
  /** «La sesión volvió a Software · 05/10 18:37». */
  titulo: string
  /** «Se cerró por inactividad del cliente.» — '' si no se guardó la causa. */
  porque: string
  /** Qué pasa si se escribe, y cómo vuelve a habilitarse. */
  queHacer: string
}

/**
 * Lo que se le dice al asesor, o `null` si se le puede escribir al cliente.
 *
 * Al cumplirse las 48 h (`ventana_vencida`) lo dice con todas las letras: «se
 * cumplieron las 48 h y el cliente está en el precalificador». Regla de Ruben
 * del 06-oct-2026: quien abre ese chat —en la bandeja o en En vivo— no puede
 * hacer nada con él, y tiene que saberlo antes de intentarlo.
 */
export function avisoDeSesionDevuelta(
  conv: { sesion_socio?: SesionSocio | null },
): AvisoDeSesionDevuelta | null {
  if (!sinCanalConElCliente(conv)) return null
  const s = conv.sesion_socio as SesionSocio
  const socio = nombreDelSocio(s.socio)
  const cuando = fechaHoraEnLima(s.devuelta_at || undefined)
  const causa = CAUSAS_DE_DEVOLUCION[s.causa || '']
  const vencida = s.causa === 'ventana_vencida'
  return {
    chip: vencida
      ? `${HORAS_DE_LA_SESION} h cumplidas · en precalificador`
      : `Devuelta a ${socio}`,
    titulo: vencida
      ? `El cliente está en el precalificador de ${socio}${cuando ? ` desde el ${cuando}` : ''}`
      : `La sesión volvió a ${socio}${cuando ? ` · ${cuando}` : ''}`,
    porque: causa ? `${causa.charAt(0).toUpperCase()}${causa.slice(1)}.` : '',
    queHacer:
      `El número de WhatsApp es de ${socio} y ahora le contesta su bot: lo que `
      + 'escribas desde acá no le llega al cliente. Si el cliente vuelve a '
      + `escribir y ${socio} nos pasa la conversación de nuevo, se habilita sola.`,
  }
}

// ── Hasta cuándo es nuestra: el fin de la sesión y la extensión ────────────
//
// Regla de Ruben del 06-oct-2026. La sesión que nos pasa Software es nuestra
// 48 h desde el pase (24 h hasta el 22-oct-2026), la atienda el agente o un
// asesor; al cumplirse vuelve a
// su precalificador y, si la atendía una persona, la conversación se marca
// resuelta sola. Hasta ese día, con un asesor no volvía nunca: si no la
// marcaba resuelta, el cliente le escribía a Software y nadie le contestaba.
// Al borde, el asesor la puede extender hasta 2 h, una vez, y nunca más allá
// de la ventana de WhatsApp del cliente. Las reglas son del backend
// (`shared/fin_de_sesion.py`); acá solo se redacta.

/**
 * Por qué no se puede extender, en palabras del asesor. Las claves son los
 * `fin_de_sesion.MOTIVO_*` del backend, que también viajan en el `error` del
 * 409 de `extend-session`. Cruzadas por test.
 */
export const POR_QUE_NO_SE_EXTIENDE: Record<string, string> = {
  session_not_in_human_handling:
    'La atiende el agente: la sesión se extiende solo mientras la atiende una persona.',
  session_already_extended: 'Ya se extendió una vez y no se puede extender de nuevo.',
  // Con 24 h de sesión «no escribe hace casi 24 h» era cierto; con 48 h puede
  // no escribir hace 40. Lo que vale siempre es que la ventana cierra antes.
  session_extension_outside_window:
    'La ventana de WhatsApp del cliente (24 h desde su último mensaje) se cierra '
    + 'antes que la sesión: no hay a qué extenderla.',
  session_already_ended: 'La sesión ya terminó.',
  session_without_handoff: 'No hay un pase del socio que extender.',
}

/** «15:42», en hora de Lima. */
export function horaEnLima(ts: string | null | undefined): string {
  return fechaHoraEnLima(ts).slice(6)
}

// ── La sesión es nuestra y la ventana de WhatsApp se cerró ─────────────────
//
// La sesión dura 48 h desde el pase; la ventana de WhatsApp, 24 h desde el
// último mensaje del cliente. Con 24 h de sesión las dos cerraban casi juntas.
// Desde el 22-oct-2026, el cliente que no escribe en un día deja la ventana
// cerrada con la sesión todavía nuestra, hasta un día entero, y el portal le
// ofrecía al asesor «Reactivar con plantilla aprobada». Por el canal del socio
// no sale ninguna: las plantillas son de NUESTRA cuenta de WhatsApp y el bot
// las rechaza (`_send_whatsapp_template`, `not_applicable_on_partner_channel`).

/** La sesión del socio sigue siendo nuestra: por su canal no salen plantillas. */
export function sinPlantillas(conv: { sesion_socio?: SesionSocio | null }): boolean {
  return conv.sesion_socio?.estado === 'activa'
}

/**
 * Qué decirle al asesor con la ventana cerrada y la sesión del socio todavía
 * nuestra, o `null` si no es el caso (ahí vale la plantilla de siempre).
 */
export function ventanaCerradaConSocio(
  conv: { sesion_socio?: SesionSocio | null }, nombre: string, desconocida = false,
): string | null {
  if (!sinPlantillas(conv)) return null
  const s = conv.sesion_socio as SesionSocio
  const socio = nombreDelSocio(s.socio)
  const fin = fechaHoraEnLima(s.fin?.fin_at || undefined)
  const siNo = fin
    ? `; si no, el ${fin.slice(0, 5)} a las ${fin.slice(6)} vuelve al precalificador de ${socio}`
    : ''
  return (desconocida
    ? `No sabemos cuándo escribió ${nombre} por última vez, y sin eso WhatsApp no deja `
      + 'mandarle mensajes libres. '
    : `${nombre} no escribe hace más de 24 h y WhatsApp bloquea los mensajes libres. `)
    + `La conversación entró por ${socio} y por ese canal no se mandan plantillas desde el `
    + `portal. Si el cliente vuelve a escribir, el chat se habilita solo${siNo}.`
}

/** Lo que queda cuando se cierre la ventana: la plantilla, o esperar al cliente. */
export function despuesDeLaVentana(conv: { sesion_socio?: SesionSocio | null }): string {
  if (!sinPlantillas(conv)) return 'Pasado ese plazo solo podrás enviar una plantilla aprobada.'
  const socio = nombreDelSocio(conv.sesion_socio?.socio)
  return `Pasado ese plazo no podrás escribirle hasta que vuelva a escribir: la conversación `
    + `entró por ${socio} y por ese canal no se mandan plantillas.`
}

/** «25 min», «1 h 10 min», «2 h». */
export function duracionLegible(minutos: number): string {
  const m = Math.max(0, Math.round(minutos))
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h && r) return `${h} h ${r} min`
  if (h) return `${h} h`
  return `${r} min`
}

export interface AvisoFinDeSesion {
  /** El chip de la cabecera: «Sesión con Software hasta 07/10 15:42». */
  chip: string
  /** Faltan 30 min o menos. */
  porVencer: boolean
  /** La atiende una persona: a ella va el aviso y la extensión. */
  enManosHumanas: boolean
  /** «La sesión con Software termina en 25 min (15:42)». */
  titulo: string
  /** Qué pasa a esa hora. */
  queVaAPasar: string
  puedeExtender: boolean
  /** «Extender hasta las 17:42». '' si no se puede. */
  boton: string
  /** Por qué la extensión no llega a las 2 h, o '' si llega. */
  tope: string
  /** Por qué no se puede extender, o '' si se puede. */
  porQueNo: string
}

/**
 * Lo que se le dice al asesor sobre el fin de la sesión, o `null` si la
 * conversación no tiene una sesión de socio activa (o el backend no mandó
 * `fin`: el listado no lo trae).
 */
export function avisoDeFinDeSesion(
  conv: { sesion_socio?: SesionSocio | null },
): AvisoFinDeSesion | null {
  const s = conv.sesion_socio
  const f = s?.fin
  if (!s || s.estado !== 'activa' || !f) return null
  const socio = nombreDelSocio(s.socio)
  const hora = horaEnLima(f.fin_at)
  const enManosHumanas = f.motivo_no_extiende !== 'session_not_in_human_handling'
  const cuanto = f.minutos_restantes <= 0
    ? 'ahora'
    : `en ${duracionLegible(f.minutos_restantes)}`
  const maximo = duracionLegible(f.extension_max_min)

  let tope = ''
  if (f.puede_extender && f.extender_hasta) {
    const pedido = Date.parse(f.fin_base_at) + f.extension_max_min * 60_000
    if (Date.parse(f.extender_hasta) < pedido) {
      tope = `No llega a ${maximo}: la ventana de WhatsApp del cliente se cierra a las `
        + `${horaEnLima(f.extender_hasta)}. Si vuelve a escribir, la extensión se estira `
        + `sola hasta las ${horaEnLima(new Date(pedido).toISOString())}.`
    }
  }

  return {
    chip: f.por_vencer
      ? `Vuelve a ${socio} ${cuanto}`
      : `Sesión con ${socio} hasta ${fechaHoraEnLima(f.fin_at)}`,
    porVencer: f.por_vencer,
    enManosHumanas,
    titulo: f.extendida
      ? `La sesión extendida con ${socio} termina ${cuanto} (${hora})`
      : `La sesión con ${socio} termina ${cuanto} (${hora})`,
    queVaAPasar: enManosHumanas
      ? `A esa hora la conversación vuelve al precalificador de ${socio} y se marca `
        + 'resuelta sola: desde ahí lo que escribas ya no le llega al cliente.'
      : `A esa hora la conversación vuelve al precalificador de ${socio}.`,
    puedeExtender: f.puede_extender,
    boton: f.puede_extender && f.extender_hasta
      ? `Extender hasta las ${horaEnLima(f.extender_hasta)}`
      : '',
    tope,
    porQueNo: f.puede_extender ? '' : (POR_QUE_NO_SE_EXTIENDE[f.motivo_no_extiende || ''] ?? ''),
  }
}
